import { createFileRoute } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { requireAuth } from '@/lib/auth-guard'
import { repositories } from '@/data'
import { pickAgency } from '@/modules/launch/use-current-agency'

export const Route = createFileRoute('/$orgSlug/book')({
  beforeLoad: () => requireAuth(),
  // `?view=<id>` opens a saved view — what a snapshot's "Copy link" produces.
  validateSearch: (raw: Record<string, unknown>): { view?: string } =>
    typeof raw['view'] === 'string' && raw['view'] ? { view: raw['view'] } : {},
  // The page needs the agency, then the fleet and the views — a three-step
  // waterfall if left to the component, which also waits for its own lazy
  // chunk first. Resolve the agency here (cached a minute) and start the other
  // two in flight; the page then finds them ready or nearly so. Not awaited:
  // the page has skeletons for the gap, and holding navigation would feel worse.
  loader: async ({ context, params }) => {
    const { queryClient } = context as { queryClient: QueryClient }
    const agencies = await queryClient.ensureQueryData({
      queryKey: ['agencies'],
      queryFn: () => repositories.auth.listAgencies(),
      staleTime: 60_000,
    })
    const agencyId = pickAgency(agencies, params.orgSlug)?.id ?? null
    if (agencyId === null) return
    void queryClient.prefetchQuery({
      queryKey: ['fleet', agencyId],
      queryFn: () => repositories.launchProjection.getFleet(agencyId),
      staleTime: 5 * 60_000,
    })
    void queryClient.prefetchQuery({
      queryKey: ['fleet-views', agencyId],
      queryFn: () => repositories.launchProjection.listFleetViews(agencyId),
      staleTime: 5 * 60_000,
    })
  },
})
